/**
 * App install screen
 *
 * Route: `/webAppOnboarding/install`. Deep link to mobile app and course image marquee;
 * final step for blocked, Abtretung, and invoice flows.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` only — intentionally outside
 *   `OnboardingCompletedGuard` so completed patients can stay on install.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: no completed redirect (this is the destination for completed onboarding).
 * - Screen-local: `persistOnboardingCompletion()` on mount; `useSaveOnboardingStep('install')`;
 *   no back navigation (ERS-61 G7 — this is the last screen of the flow).
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { maxOnboardingContentWidth, space } from '../../../styles/onboardingUiTokens';
import { DualPhotoMarquee } from '../components/content/DualPhotoMarquee';
import { Notice } from '../components/elements/Notice';
import { ForwardButton } from '../components/layout/ForwardButton';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { Body } from '../components/typography/Body';
import { H1 } from '../components/typography/HeadingLevels';
import { resolveInstallMarqueeColumns, useCourseContent } from '../hooks/useCourseContent';
import { resolveOnboardingTotalSteps } from '../insurance/config';
import { resolveBillingTypeFromPatient } from '../insurance/routing';
import { persistOnboardingCompletion, useSaveOnboardingStep } from '../services/onboardingProgress';
import {
  getAppDeepLink,
  getSignedUpPatient,
  getSignUpId,
  getVerifiedPatientNamesFromAuth,
  useOnboardingStore,
} from '../store/onboardingStore';
import { resolveAppInstallLink } from '../utils/appInstallLink';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg};
`;

const MarqueeStage = styled.div`
  position: relative;
  width: 100%;
`;

const InstallCtaOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space.lg};
  box-sizing: border-box;
  pointer-events: none;

  & > * {
    pointer-events: auto;
    width: 100%;
    max-width: ${maxOnboardingContentWidth};
    min-width: 0;
  }
`;

export function WebOnboardingInstallScreen() {
  useSaveOnboardingStep('install');
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);
  const verifiedNames = useOnboardingStore(getVerifiedPatientNamesFromAuth);
  const appDeepLink = useOnboardingStore(getAppDeepLink);
  const signUpId = useOnboardingStore(getSignUpId);
  const completionPersistedRef = useRef(false);

  const contentCourseId = signedUpPatient?.course?.id?.trim() ?? '';
  const courseContent = useCourseContent(contentCourseId);
  const [courseImageErrorsByKey, setCourseImageErrorsByKey] = useState<Record<string, number>>({});
  const courseImageErrorKey = `${contentCourseId}:${courseContent.status}`;
  const courseImageErrorCount = courseImageErrorsByKey[courseImageErrorKey] ?? 0;

  useEffect(() => {
    if (!signedUpPatient || completionPersistedRef.current) {
      return;
    }
    completionPersistedRef.current = true;
    persistOnboardingCompletion();
  }, [signedUpPatient]);

  const firstName = useMemo(() => {
    const name = verifiedNames?.firstName?.trim() || signedUpPatient?.patientFirstName?.trim() || '';
    return name || 'du';
  }, [verifiedNames?.firstName, signedUpPatient?.patientFirstName]);

  const { leftImages, rightImages, imageSource } = useMemo(
    () => resolveInstallMarqueeColumns(courseContent, courseImageErrorCount),
    [courseContent, courseImageErrorCount],
  );

  const useCourseUrls = imageSource === 'course';

  const handleMarqueeImageError = useCallback(() => {
    setCourseImageErrorsByKey((prev) => ({
      ...prev,
      [courseImageErrorKey]: (prev[courseImageErrorKey] ?? 0) + 1,
    }));
  }, [courseImageErrorKey]);

  const installLink = useMemo(() => resolveAppInstallLink(appDeepLink, signUpId), [appDeepLink, signUpId]);
  const ctaDisabled = !installLink;

  const totalSteps = resolveOnboardingTotalSteps(resolveBillingTypeFromPatient(signedUpPatient));

  const handleInstall = useCallback(() => {
    if (!installLink) {
      return;
    }
    const billingType = signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType;
    // `linkSource` measures how often the fallback actually carries a patient. Anything above noise
    // in production means an invitation path stopped minting Branch links — go and find it rather
    // than letting the fallback absorb it.
    const eventProps = { ...(billingType ? { billingType } : {}), linkSource: installLink.source };
    // sendBeacon: the captures are immediately followed by a full-page navigation to the Branch/app-store
    // link (`window.location.href`). Without sendBeacon the queued XHR is aborted on unload and the events
    // are lost (PF-4982: web_install_cta_clicked / web_install_completed never arrived).
    posthog?.capture(PATIENT_TRACKING_EVENTS.web_install_cta_clicked, eventProps, {
      transport: 'sendBeacon',
    });
    persistOnboardingCompletion();
    posthog.capture(PATIENT_TRACKING_EVENTS.web_install_completed, eventProps, {
      transport: 'sendBeacon',
    });
    window.location.href = installLink.url;
  }, [installLink, posthog, signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType]);

  const bodyContent = (
    <ContentColumn>
      <H1>Glückwunsch {firstName}! Dein Kurs ist bereit.</H1>
      <Body variant="document">
        Installiere nun die App auf deinem Handy oder Tablet und starte deinen Kurs. Wir wünschen dir viel Spaß dabei.
      </Body>
      <MarqueeStage>
        <DualPhotoMarquee
          leftImages={leftImages}
          rightImages={rightImages}
          height="calc(100dvh - 200px)"
          style={{ opacity: 0.35 }}
          imageLoading="eager"
          onImageError={useCourseUrls ? handleMarqueeImageError : undefined}
        />
        <InstallCtaOverlay>
          <ForwardButton type="button" size="extraLarge" disabled={ctaDisabled} onClick={() => void handleInstall()}>
            App installieren
          </ForwardButton>
        </InstallCtaOverlay>
      </MarqueeStage>
      {!installLink ? (
        // Only reachable with no signUpId at all. The old copy sent the patient to their practice,
        // who cannot mint a Branch link either; support is the one exit that can help, matching how
        // `signUpGateErrorCopy` routes unrecoverable link problems.
        <Notice preset="warning" title="App-Link">
          Der Installationslink konnte nicht erzeugt werden. Öffne bitte den Link aus deiner SMS erneut oder melde
          dich bei unserem Support.
        </Notice>
      ) : null}
    </ContentColumn>
  );

  return (
    <OnboardingLayout currentStep={totalSteps} totalSteps={totalSteps} hideForward>
      <OnboardingBody disableSupportHint style={{ paddingBottom: 0 }}>
        {bodyContent}
      </OnboardingBody>
    </OnboardingLayout>
  );
}
